import { Badge, Card, Heading, RadioCard, Stack, vars, Text, textVariants } from '@etg/wings';
import { Notification, variants } from '@eti/components';
import { useDirection, useProperty, useTranslation } from '@eti/providers';
import { css, mediaQueries } from '@eti/styles';
import type { BrandedFare as BrandedFareType } from '@eti/schema-types';
import { price as priceUtil } from '@eti/utils';
import { useMemo, forwardRef } from 'react';
import { formatBrandedFareName, getFormattedFarePrice } from '../utils';
import { INCLUDED_PERSONAL_ITEM } from '../../../../../constants/includedBaggageConstants';
import { screenReadersOnlyStyles } from '../../../../../utils/styleUtils';
import CheckedBaggageFeature from './features/CheckedBaggageFeature';
import CabinBaggageFeature from './features/CabinBaggageFeature';
import BrandedFareFeature from './features/BrandedFareFeature';
import PersonalItemAndCabinBaggageFeature from './features/PersonalItemAndCabinBaggageFeature';
import usePriceDisplayType from './hooks/usePriceDisplayType';

const cardStyles = (isRecommended: boolean) => css`
  position: relative;

  @media ${mediaQueries.small.only} {
    ${isRecommended && `margin-top: 8px;`}
  }

  & > div {
    display: flex;
    flex-direction: column;
  }

  label {
    padding: 10px 0 4px;
  }
`;

const featureSectionStyles = css`
  border-top: 1px solid ${vars.colors.divider};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const featureListStyles = css`
  margin-top: 8px;
`;

const notificationStyles = css`
  margin-top: 16px;
`;

const badgeStyles = css`
  display: inline-block;
  left: 0;
  margin: 0 auto;
  position: absolute;
  right: 0;
  top: -16px;
  width: fit-content;
`;

const cardSectionStyles = css`
  border-bottom: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

interface BrandedFareProps {
  brandedFareData: BrandedFareType;
  recommendedFareKey: string;
  shouldShowInformationBox?: boolean;
  selectedTripPricePerAdult: number;
  ticketingCarrierCodes: string[];
}

const BrandedFare = forwardRef<HTMLInputElement, BrandedFareProps>(
  (
    {
      brandedFareData,
      recommendedFareKey,
      selectedTripPricePerAdult,
      shouldShowInformationBox = false,
      ticketingCarrierCodes = [],
    }: BrandedFareProps,
    ref,
  ) => {
    const { t } = useTranslation();
    const { p } = useProperty();
    const { isRTL } = useDirection();

    const { priceDisplayType, priceDisplayText, priceToAnnounceText } = usePriceDisplayType();
    const informationBoxText = t('BrandedFares.BasicOption.Information.Text');
    const carriersNotIncludedProducts: string[] = p('BrandedFares.ShowNotIncluded.Carriers') || [];
    const shouldUseCabinBaggageTextUsingAtpco = p('BrandedFares.CabinBaggage.From.Atpco.Enabled');
    const shouldShowNotIncludedProducts = carriersNotIncludedProducts.some((item) =>
      ticketingCarrierCodes.includes(item),
    );
    const shouldShowPersonalItem = p('BrandedFares.ShowPersonalBaggage.Enabled');
    const isRecommended = recommendedFareKey === brandedFareData.selectionKey;

    const shouldDisplayIncrementalPricing = p('BrandedFares.IncrementalPricing.Enabled');

    const formattedFarePrice = useMemo(() => {
      return getFormattedFarePrice(
        brandedFareData,
        priceDisplayType,
        selectedTripPricePerAdult,
        shouldDisplayIncrementalPricing,
        isRTL,
      );
    }, [
      brandedFareData,
      priceDisplayType,
      selectedTripPricePerAdult,
      shouldDisplayIncrementalPricing,
      isRTL,
    ]);

    const personalItemTripSummary =
      brandedFareData?.personalItemTripSummary?.personalItemTravelerDetails[0];
    const isPersonalItemIncluded =
      personalItemTripSummary?.personalItemStatus === INCLUDED_PERSONAL_ITEM;

    const priceWithCurrencyName = priceUtil.replaceWithCurrencyName(formattedFarePrice);
    let priceToRead = `${priceWithCurrencyName} ${priceToAnnounceText}`;
    if (shouldDisplayIncrementalPricing) {
      const regexp = new RegExp(`^-`);
      const isFareAtDiscount = regexp.test(formattedFarePrice);
      if (isFareAtDiscount) {
        priceToRead = t(
          'BrandedFares.IncrementalPricing.FareAtDiscount.Text',
          priceWithCurrencyName.replace('-', ''),
          priceToAnnounceText,
        );
      } else {
        priceToRead = t(
          'BrandedFares.IncrementalPricing.FareAtExtraPrice.Text',
          priceWithCurrencyName.replace('+', ''),
          priceToAnnounceText,
        );
      }
    }

    return (
      <RadioCard
        className={cardStyles(isRecommended)}
        data-testid={`brandedFare-option-${brandedFareData.id}`}
        id={brandedFareData.id}
        key={brandedFareData.selectionKey}
        label={
          <>
            <Heading as="span" level={3}>
              {formatBrandedFareName(brandedFareData.name)}
            </Heading>
            {isRecommended && (
              <Badge className={badgeStyles} variant="promotional">
                {t('BrandedFares.Recommended.Text')}
              </Badge>
            )}
          </>
        }
        ref={ref}
        value={brandedFareData.selectionKey}
      >
        <Card.Section className={cardSectionStyles} role="region">
          <Text
            aria-hidden
            data-testid={`brandedFare-${brandedFareData.id}-price`}
            variant={textVariants._24_EMPHASIZED}
          >
            {formattedFarePrice}
          </Text>
          <Text aria-hidden as="p">
            {priceDisplayText}
          </Text>
          <Text className={screenReadersOnlyStyles}>{priceToRead}</Text>
        </Card.Section>
        <div className={featureSectionStyles}>
          <Stack
            as="ul"
            className={featureListStyles}
            key={`${brandedFareData.selectionKey}`}
            spacing={8}
          >
            {!shouldUseCabinBaggageTextUsingAtpco && shouldShowPersonalItem && (
              <PersonalItemAndCabinBaggageFeature
                brandedFareData={brandedFareData}
                isPersonalItemIncluded={isPersonalItemIncluded}
                shouldShowNotIncludedProducts={shouldShowNotIncludedProducts}
              />
            )}

            {!shouldUseCabinBaggageTextUsingAtpco && !shouldShowPersonalItem && (
              <CabinBaggageFeature
                brandedFareData={brandedFareData}
                shouldShowNotIncludedProducts={shouldShowNotIncludedProducts}
              />
            )}

            <CheckedBaggageFeature
              brandedFareData={brandedFareData}
              shouldShowNotIncludedProducts={shouldShowNotIncludedProducts}
            />
            {brandedFareData?.features?.map((feature) => (
              <BrandedFareFeature
                brandedFareData={brandedFareData}
                feature={feature!}
                key={feature?.code + brandedFareData.selectionKey}
                shouldShowNotIncludedProducts={shouldShowNotIncludedProducts}
              />
            ))}
          </Stack>
          {shouldShowInformationBox && informationBoxText && (
            <Notification
              className={notificationStyles}
              dataTestId="brandedFares-firstOption-notification"
              variant={variants.INFORMATION}
            >
              {informationBoxText}
            </Notification>
          )}
        </div>
      </RadioCard>
    );
  },
);

export default BrandedFare;
